import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router';
import { AlertTriangle, Boxes, Trash2, X } from 'lucide-react';
import { api, ApiError } from '../api/cliente.js';
import { Pill, tomDeStatus } from '../componentes/ui/Pill.js';
import { Botao } from '../componentes/ui/Botao.js';
import { Modal } from '../componentes/ui/Modal.js';
import { ModalImportarCidr } from './blocos/ModalImportarCidr.js';

interface ZonaBloco {
  zona: string;
  criadaNoPdns: boolean;
  delegada: boolean;
}

interface Bloco {
  id: string;
  cidr: string;
  versao: 'V4' | 'V6';
  classless: boolean;
  requerAprovacao: boolean;
  operadora: { id: string; nome: string };
  zonas: ZonaBloco[];
}

type Filtro = 'todos' | 'v4' | 'v6' | 'pendentes';

export function Blocos() {
  const [filtro, setFiltro] = useState<Filtro>('todos');
  const [importando, setImportando] = useState(false);
  const [removendo, setRemovendo] = useState<Bloco | null>(null);
  const qc = useQueryClient();

  // Vindo de "Ver blocos" na tela de Operadoras: mostra só os blocos dela.
  const [searchParams, setSearchParams] = useSearchParams();
  const operadoraFiltro = searchParams.get('operadora');

  const { data, isLoading, error } = useQuery({
    queryKey: ['blocos'],
    queryFn: () => api.get<Bloco[]>('/blocos'),
  });

  const recarregar = () => {
    void qc.invalidateQueries({ queryKey: ['blocos'] });
    void qc.invalidateQueries({ queryKey: ['zonas'] });
    void qc.invalidateQueries({ queryKey: ['dashboard'] });
  };

  // A operadora é o recorte de base; os chips de versão contam dentro dela.
  const base = operadoraFiltro
    ? (data ?? []).filter((b) => b.operadora.id === operadoraFiltro)
    : (data ?? []);

  const nomeOperadora = base[0]?.operadora.nome ?? null;

  const passaFiltro = (b: Bloco, f: Filtro) => {
    if (f === 'v4') return b.versao === 'V4';
    if (f === 'v6') return b.versao === 'V6';
    if (f === 'pendentes') return b.zonas.some((z) => !z.criadaNoPdns || !z.delegada);
    return true;
  };

  const blocos = base.filter((b) => passaFiltro(b, filtro));
  const contar = (f: Filtro) => base.filter((b) => passaFiltro(b, f)).length;

  const limparOperadora = () => {
    const p = new URLSearchParams(searchParams);
    p.delete('operadora');
    setSearchParams(p, { replace: true });
  };

  if (isLoading) return <Esqueleto />;

  if (error) {
    return (
      <div className="rounded-card border border-erro/20 bg-erro-fundo p-6 text-center">
        <AlertTriangle className="mx-auto mb-2 text-erro" size={22} />
        <p className="text-[14px] font-bold text-erro">Não foi possível carregar os blocos</p>
        <p className="mt-1 text-[13px] text-erro/80">{(error as Error).message}</p>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {operadoraFiltro && (
        <div className="flex flex-wrap items-center gap-2 rounded-card border border-marca/30 bg-tint/40 px-4 py-2.5">
          <Boxes size={15} className="text-marca" />
          <span className="text-[13px]">
            Mostrando os blocos de <strong>{nomeOperadora ?? 'uma operadora'}</strong>.
          </span>
          <button
            onClick={limparOperadora}
            className="ml-auto inline-flex items-center gap-1 text-[12.5px] font-semibold text-texto-suave hover:text-marca"
          >
            <X size={13} /> ver todos
          </button>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <Botao
          variante="primario"
          className="order-last ml-auto"
          onClick={() => setImportando(true)}
        >
          Importar CIDR
        </Botao>
        {([
          ['todos', 'Todos'],
          ['v4', 'IPv4'],
          ['v6', 'IPv6'],
          ['pendentes', 'Com pendência'],
        ] as const).map(([chave, rotulo]) => {
          const ativo = filtro === chave;
          return (
            <button
              key={chave}
              onClick={() => setFiltro(chave)}
              className={`flex h-10 items-center gap-2 rounded-controle border px-4 text-[13px] font-bold transition-colors ${
                ativo
                  ? 'border-marca bg-tint text-marca'
                  : 'border-borda bg-superficie text-texto-medio hover:border-marca'
              }`}
            >
              {rotulo}
              <span
                className={`rounded-pill px-2 py-px text-[11px] ${
                  ativo ? 'bg-marca text-white' : 'bg-borda-suave text-texto-suave'
                }`}
              >
                {contar(chave)}
              </span>
            </button>
          );
        })}
      </div>

      {blocos.length === 0 ? (
        <VazioBlocos
          temFiltro={filtro !== 'todos' || !!operadoraFiltro}
          aoImportar={() => setImportando(true)}
        />
      ) : (
        <div className="overflow-hidden rounded-card border border-borda bg-superficie">
          {/* Rolagem horizontal própria: a tabela não cabe em telas estreitas,
              e a página inteira nunca deve rolar de lado. */}
          <div className="overflow-x-auto">
            <table className="w-full min-w-[720px] border-collapse">
              <thead>
                <tr className="border-b border-borda bg-borda-tenue">
                  {['Bloco CIDR', 'Operadora', 'Versão', 'Zonas reversas', 'Situação', ''].map((h) => (
                    <th
                      key={h}
                      className="px-[18px] py-3 text-left text-[11px] font-bold uppercase tracking-[0.6px] text-texto-suave"
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {blocos.map((b) => {
                  const pendentes = b.zonas.filter((z) => !z.criadaNoPdns).length;
                  const situacao = pendentes > 0 ? 'Pendente' : 'Sincronizado';
                  return (
                    <tr key={b.id} className="border-t border-borda-suave hover:bg-borda-tenue">
                      <td className="px-[18px] py-[13px]">
                        <span className="font-mono text-[13.5px] font-semibold">{b.cidr}</span>
                        {b.classless && (
                          // Precisa aparecer na listagem: um bloco menor que /24
                          // não resolve sem o upstream configurar RFC 2317, e
                          // descobrir isso só depois de cadastrar custa caro.
                          <Pill tom="aviso" className="ml-2">
                            RFC 2317
                          </Pill>
                        )}
                      </td>
                      <td className="px-[18px] py-[13px] text-[13.5px]">{b.operadora.nome}</td>
                      <td className="px-[18px] py-[13px]">
                        <Pill tom={b.versao === 'V6' ? 'ipv6' : 'info'} mono>
                          {b.versao === 'V6' ? 'IPv6' : 'IPv4'}
                        </Pill>
                      </td>
                      <td className="px-[18px] py-[13px]">
                        <div className="flex flex-wrap gap-1">
                          {b.zonas.map((z) => (
                            <span
                              key={z.zona}
                              title={z.criadaNoPdns ? 'Criada no PowerDNS' : 'Ainda não criada'}
                              className={`rounded-chip px-2 py-0.5 font-mono text-[11.5px] ${
                                z.criadaNoPdns
                                  ? 'bg-sucesso-fundo text-sucesso'
                                  : 'bg-aviso-fundo text-aviso'
                              }`}
                            >
                              {z.zona}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td className="px-[18px] py-[13px]">
                        <Pill tom={tomDeStatus(situacao)}>{situacao}</Pill>
                      </td>
                      <td className="px-[18px] py-[13px]">
                        <div className="flex justify-end gap-1.5">
                          <Link
                            to={`/blocos/${b.id}/ptr`}
                            className="inline-flex h-8 items-center rounded-chip border border-borda px-3 text-[12.5px] font-bold text-texto-medio hover:border-marca hover:text-marca"
                          >
                            Gerenciar PTR
                          </Link>
                          <button
                            onClick={() => setRemovendo(b)}
                            aria-label={`Remover ${b.cidr}`}
                            className="grid size-8 place-items-center rounded-chip text-texto-mudo hover:bg-erro-fundo hover:text-erro"
                          >
                            <Trash2 size={15} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {importando && (
        <ModalImportarCidr aoFechar={() => setImportando(false)} aoCriar={recarregar} />
      )}
      {removendo && (
        <ModalRemover
          bloco={removendo}
          aoFechar={() => setRemovendo(null)}
          aoRemover={recarregar}
        />
      )}
    </div>
  );
}

/** Estado vazio — não estava no protótipo, o handoff pediu que fosse criado. */
function VazioBlocos({
  temFiltro,
  aoImportar,
}: {
  temFiltro: boolean;
  aoImportar: () => void;
}) {
  return (
    <div className="rounded-card border border-borda bg-superficie px-6 py-14 text-center">
      <div className="mx-auto mb-3 grid size-12 place-items-center rounded-card bg-tint text-marca">
        <Boxes size={24} />
      </div>
      <p className="text-[15px] font-extrabold">
        {temFiltro ? 'Nenhum bloco neste filtro' : 'Nenhum bloco cadastrado'}
      </p>
      <p className="mx-auto mt-1 max-w-[380px] text-[13px] text-texto-suave">
        {temFiltro
          ? 'Tente outro filtro para ver os demais blocos.'
          : 'Importe um bloco CIDR para que o painel crie as zonas reversas e comece a gerenciar os PTRs.'}
      </p>
      {!temFiltro && (
        <Botao variante="primario" className="mt-5" onClick={aoImportar}>
          Importar CIDR
        </Botao>
      )}
    </div>
  );
}

/**
 * Remoção de bloco.
 *
 * A escolha sobre as zonas é explícita e o padrão é preservá-las: elas contêm
 * os PTRs que estão no ar, e apagá-las junto com o cadastro derrubaria o
 * reverso de todos aqueles endereços num clique.
 */
function ModalRemover({
  bloco,
  aoFechar,
  aoRemover,
}: {
  bloco: Bloco;
  aoFechar: () => void;
  aoRemover: () => void;
}) {
  const [removerZonas, setRemoverZonas] = useState(false);
  const [confirmacao, setConfirmacao] = useState('');

  const remover = useMutation({
    mutationFn: () => api.delete(`/blocos/${bloco.id}?removerZonas=${removerZonas}`),
    onSuccess: () => {
      aoRemover();
      aoFechar();
    },
  });

  return (
    <Modal
      aberto
      aoFechar={aoFechar}
      titulo="Remover bloco"
      descricao={bloco.cidr}
      rodape={
        <>
          <Botao onClick={aoFechar}>Cancelar</Botao>
          <Botao
            variante="perigo"
            onClick={() => remover.mutate()}
            carregando={remover.isPending}
            disabled={confirmacao !== bloco.cidr}
          >
            Remover
          </Botao>
        </>
      }
    >
      <div className="space-y-4">
        <label className="flex cursor-pointer items-start gap-3 rounded-chip border border-borda p-3">
          <input
            type="checkbox"
            checked={removerZonas}
            onChange={(e) => setRemoverZonas(e.target.checked)}
            className="mt-0.5 size-4 shrink-0 accent-erro"
          />
          <span>
            <span className="block text-[13.5px] font-bold">
              Remover também as {bloco.zonas.length} zona(s) do PowerDNS
            </span>
            <span className="block text-[12px] leading-snug text-texto-suave">
              Isso apaga os reversos que estão no ar. Deixando desmarcado, as zonas
              permanecem e aparecem como órfãs na tela de Sincronização.
            </span>
          </span>
        </label>

        <label className="block">
          <span className="mb-1.5 block text-[11px] font-bold uppercase tracking-[0.6px] text-texto-suave">
            Digite <span className="font-mono">{bloco.cidr}</span> para confirmar
          </span>
          <input
            value={confirmacao}
            onChange={(e) => setConfirmacao(e.target.value)}
            autoFocus
            spellCheck={false}
            className="h-11 w-full rounded-controle border border-borda px-3 font-mono text-[13.5px] focus:border-erro focus:outline-none"
          />
        </label>

        {remover.error && (
          <p className="rounded-chip bg-erro-fundo px-3 py-2 text-[12.5px] font-semibold text-erro">
            {(remover.error as ApiError).mensagem}
          </p>
        )}
      </div>
    </Modal>
  );
}

function Esqueleto() {
  return (
    <div className="space-y-5">
      <div className="flex gap-2">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="h-10 w-28 animate-pulse rounded-controle bg-borda" />
        ))}
      </div>
      <div className="rounded-card border border-borda bg-superficie p-4">
        {[0, 1, 2, 3, 4].map((i) => (
          <div key={i} className="h-12 animate-pulse border-b border-borda-suave last:border-0">
            <div className="h-3 w-1/3 translate-y-4 rounded bg-borda" />
          </div>
        ))}
      </div>
    </div>
  );
}
